import { useQueryClient } from "@tanstack/react-query";
import { isEmpty } from "lodash";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { useMobile } from "@krea/customer-features/hooks";
import { settings } from "@krea/customer-features/settings";
import {
  ApplicationForm,
  FORM_STEP,
  useApplicationFormNavigation,
} from "@krea/customer-features/shared-components/application-form";
import IdentificationStep from "@krea/customer-features/shared-components/application-form/IdentificationStep";
import { useApplicationFlowForm } from "@krea/customer-features/shared-components/application-form/hooks/useApplicationFlowForm";
import LoanApplicationFormLayout from "@krea/customer-features/shared-components/loan-application-form-layout";
import LoginContainer from "@krea/customer-features/shared-components/login/LoginContainer";
import Preloader from "@krea/customer-features/shared-components/preloader";
import { useBankIDStore } from "@krea/customer-features/store/bankID/hooks";
import { useLoanApplicationFormStore } from "@krea/customer-features/store/loan-application-form/hooks";
import { BANKID_AUTHENTICATE_STORE_KEY } from "@krea/customer-features/store/queryClient";
import { useGetUserData } from "@krea/customer-features/store/user/hooks/queries/useGetUserData";
import { CountryCode } from "@krea/shared/types/common";

import { useCreateApplication } from "store/loan-application-form/hooks/mutations";

export const ApplicationFormPage = () => {
  const navigate = useNavigate();
  // applicationStep router param
  const { applicationStep } = useParams();

  const { t } = useTranslation();
  const isMobile = useMobile();

  const queryClient = useQueryClient();

  const onStepChange = useCallback(
    (step: string) => {
      // This enhances the setCurrentStep function from useApplicationFormNavigation
      // by also enabling routed steps.
      navigate(`/application/${step}`);
    },
    [navigate],
  );

  const { getPreviousStep, setCurrentStep, currentStep } =
    useApplicationFormNavigation(onStepChange);

  const setCurrentStepInStore = useLoanApplicationFormStore(
    (state) => state.setCurrentStep,
  );

  const resetBankIDStore = useBankIDStore((state) => state.resetBankIDStore);

  const applicationFormProps = useApplicationFlowForm();

  const onCancel = () => {
    queryClient.resetQueries({ queryKey: [BANKID_AUTHENTICATE_STORE_KEY] });
    resetBankIDStore();
    setCurrentStep(getPreviousStep());
  };

  const onBack = () => {
    resetBankIDStore();
    setCurrentStep(getPreviousStep());
  };

  const { data: user } = useGetUserData();
  const { mutate: createApplication, isLoading } = useCreateApplication(() =>
    onCancel(),
  );

  const onSuccessBankID = useCallback(() => {
    createApplication();
  }, [createApplication]);

  const onSuccessForm = useCallback(() => {
    // If logged in, we can create the application right away
    if (!isEmpty(user)) {
      createApplication();
    }
  }, [createApplication, user]);

  useEffect(() => {
    // Scroll to top on step change on mobile
    if (isMobile) {
      window.scrollTo(0, 0);
    }
  }, [currentStep, isMobile]);

  useEffect(() => {
    // handles the "add on" routing for the application form.
    // In general, what step we are on and what step is valid to navigate to etc is handled by the ApplicationForm component

    if (applicationStep !== currentStep) {
      if (
        applicationStep &&
        Object.values(FORM_STEP).includes(applicationStep)
      ) {
        // If the route (applicationStep) is different from the currentStep, we need to update the currentStep in the store
        // This is to let the route take priority over the store

        if (applicationStep === FORM_STEP.COMPLETE) {
          // if we are navigating directly to the complete step, but currentStep is not 'complete'
          // redirect (both state and route) to the currentStep (state step)
          setCurrentStep(currentStep);
        } else {
          // if we are navigating to any other step than 'complete' We are fine to show that step
          // as the "next" button will take care of the validating/navigating prior steps in the dynamic form flow.
          setCurrentStepInStore(applicationStep);
        }
      } else {
        // Here we dont dont have a match between the route and valid 'currentStep' (FORM_STEP)
        // So we need to navigate to a correct valid step

        if (Object.values(FORM_STEP).includes(currentStep)) {
          // If we have a valid currentStep, we navigate to that step
          navigate(`/application/${currentStep}`, { replace: true });
        } else {
          // If the currentStep for some reason is not valid, we navigate to the first step
          navigate(`/application/${FORM_STEP.BASIC_DETAILS}`, {
            replace: true,
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationStep]);

  const companyName = useLoanApplicationFormStore(
    (state) => state.organisation.name,
  );

  if (isLoading) {
    return (
      <LoanApplicationFormLayout
        heading={t("applicationForm.common.identificationHeading")}
        subheading={t("applicationForm.common.weAreObligedToIdentifyYou", {
          companyName,
        })}
        upperInfoText={
          settings.countryCode === CountryCode.SE
            ? t("applicationForm.common.noApplicationOrCreditCheck")
            : t("applicationForm.common.noApplication")
        }
        innerForm={
          <div className="tw-my-7">
            <LoginContainer title={t("login.pleaseWait")} />
            <Preloader size="lg" className="tw-text-primary" />
          </div>
        }
        hideNavigation={isLoading}
        hideAdvisorCTABanner
      />
    );
  }

  return (
    <div>
      <ApplicationForm
        applicationFormProps={applicationFormProps}
        onComplete={() => onSuccessForm()}
        onStepChange={onStepChange}
      />
      {currentStep === FORM_STEP.COMPLETE && (
        <IdentificationStep
          onSuccessBankID={onSuccessBankID}
          onCancel={() => onCancel()}
          onBack={() => onBack()}
        />
      )}
    </div>
  );
};
